/**
 * Client-side matching of "Go to" suggestions. Synchronous and dependency-free:
 * the catalogue is a few dozen entries, so a ranked prefix match is enough.
 */

/** What the matcher reads from a destination; everything else rides along untouched. */
export type Matchable = {
  label: string;
  /** Space-separated extra words someone might type to find it. */
  keywords: string;
};

/** Lowercases and strips accents, so "Séan" is found by "sean" and the reverse. */
export function foldForMatch(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

export function matchWords(value: string): string[] {
  return foldForMatch(value)
    .split(/[^\p{L}\p{N}]+/u)
    .filter((word) => word !== '');
}

/** Lower is better. Order of the tiers is the ranking contract. */
const TIER = { exactLabel: 0, labelPrefix: 1, labelWord: 2, keyword: 3 } as const;

/**
 * Every query word must start some word of the label or keywords. The tier then
 * says how well the label itself answers the query: it is the whole label, the
 * start of it, made of words that start the label's words, or only reached
 * through the keywords.
 */
function tierOf(queryWords: string[], destination: Matchable): number | null {
  const labelWords = matchWords(destination.label);
  const keywordWords = matchWords(destination.keywords);
  const startsSome = (words: string[]) => (query: string) => words.some((word) => word.startsWith(query));
  if (!queryWords.every(startsSome([...labelWords, ...keywordWords]))) return null;

  const query = queryWords.join(' ');
  const label = labelWords.join(' ');
  if (query === label) return TIER.exactLabel;
  if (label.startsWith(query)) return TIER.labelPrefix;
  if (queryWords.every(startsSome(labelWords))) return TIER.labelWord;
  return TIER.keyword;
}

/**
 * The destinations that answer `query`, best first. Ties keep catalogue order,
 * so the catalogue decides which of two equally good answers leads. An empty
 * query (or one with no letters or digits) matches nothing.
 */
export function matchDestinations<T extends Matchable>(destinations: readonly T[], query: string, limit: number): T[] {
  const queryWords = matchWords(query);
  if (queryWords.length === 0) return [];
  const ranked: { destination: T; tier: number; index: number }[] = [];
  destinations.forEach((destination, index) => {
    const tier = tierOf(queryWords, destination);
    if (tier !== null) ranked.push({ destination, tier, index });
  });
  ranked.sort((a, b) => a.tier - b.tier || a.index - b.index);
  return ranked.slice(0, limit).map((entry) => entry.destination);
}
